import { useCallback, useEffect, useRef, useState } from 'react'
import { useFirebaseProvider } from '@/app/providers/useFirebaseProvider'
import { getAvatar } from '@/features/avatar-settings/api/getAvatar'
import { AVATAR_UPDATED_EVENT } from '@/features/avatar-settings/events'
import { deleteAvatarCache } from '@/shared/lib/avatarCache'
import { readAvatarFailure, reportAvatarFailure } from '@/shared/lib/avatarDiagnostics'
import { createManagedObjectUrl, retireManagedObjectUrl } from '@/shared/lib/managedObjectUrl'
import type { HubSelectionHandoff } from './hubSettingsTypes'
import { useAvatarSources } from './useAvatarSources'

const AVATAR_SYNC_ERROR_MESSAGE = 'アバターの読み込みに失敗しました。時間をおいて再度お試しください。'
const HUB_FALLBACK_MESSAGE = '設定アバターの読み込みに失敗しました。\n一時的にデフォルトアバターを表示します。'
const HUB_RETAIN_MESSAGE = 'アバターの切り替えに失敗しました。\n時間をおいて、もう一度選択してください。'
const HUB_RATE_LIMIT_MESSAGE = 'アクセスが集中しています。\n時間をおいて、もう一度選択してください。'

const isTemporaryFailure = (error: unknown): boolean => {
  const { status, code } = readAvatarFailure(error)
  if (code === 'HUB_AUTH_EXPIRED' || code === 'HUB_TERMS_CHANGED' || code === 'HUB_MODEL_UNAVAILABLE') return false
  return status === undefined || status === 429 || status >= 500
}

export type HubAvatarIssue = {
  code: 'HUB_AUTH_EXPIRED' | 'HUB_TERMS_CHANGED'
  modelId: string
  avatarVersion: string
}

const readHubErrorCode = (error: unknown): string | null =>
  typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string' ? error.code : null

type AvatarSyncState = {
  isLoading: boolean
  avatarFileUrl: string | null
  errorMessage: string | null
  hubIssue: HubAvatarIssue | null
  noticeMessage: string | null
  dismissHubIssue: () => void
  dismissNotice: () => void
  onAvatarLoadResult: (url: string, success: boolean) => void
}

export const useAvatarSync = (): AvatarSyncState => {
  const { user, isLoading: isAuthLoading } = useFirebaseProvider()
  const [isLoading, setIsLoading] = useState(true)
  const [avatarFileUrl, setAvatarFileUrl] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [hubIssue, setHubIssue] = useState<HubAvatarIssue | null>(null)
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null)
  const blobUrlRef = useRef<string | null>(null)
  const activeSyncIdRef = useRef(0)
  const displayedUrlRef = useRef<string | null>(null)

  const clearAvatarBlobUrl = useCallback((syncId?: number) => {
    if (syncId !== undefined && activeSyncIdRef.current !== syncId) return

    const previousUrl = blobUrlRef.current
    blobUrlRef.current = null
    displayedUrlRef.current = null
    setAvatarFileUrl(null)

    if (previousUrl) {
      retireManagedObjectUrl(previousUrl)
    }
  }, [])

  const setAvatarBlobUrl = useCallback((syncId: number, vrmBlob: Blob): boolean => {
    if (activeSyncIdRef.current !== syncId) return false

    const nextUrl = createManagedObjectUrl(vrmBlob)
    const previousUrl = blobUrlRef.current
    blobUrlRef.current = nextUrl
    displayedUrlRef.current = null
    setAvatarFileUrl(nextUrl)

    if (previousUrl && previousUrl !== nextUrl) {
      retireManagedObjectUrl(previousUrl)
    }

    return true
  }, [])

  const isCurrent = useCallback((syncId: number) => activeSyncIdRef.current === syncId, [])
  const onAvatarLoadResult = useCallback((url: string, success: boolean) => {
    if (blobUrlRef.current !== url) return
    displayedUrlRef.current = success ? url : null
    if (!success) {
      setIsLoading(false)
      setNoticeMessage('アバターを表示できませんでした。\n再読み込みするか、アバターを選び直してください。')
    }
  }, [])
  const { resolveDefaultAvatar, resolveUploadAvatar, resolveHubAvatar } = useAvatarSources({
    isCurrent,
    onResolved: setAvatarBlobUrl,
  })
  const showDefaultAvatar = useCallback(
    async (syncId: number, showFailureNotice = false) => {
      if (await resolveDefaultAvatar(syncId)) return
      if (!isCurrent(syncId)) return
      clearAvatarBlobUrl(syncId)
      setErrorMessage(AVATAR_SYNC_ERROR_MESSAGE)
      if (showFailureNotice)
        setNoticeMessage('デフォルトアバターの読み込みに失敗しました。時間をおいて再度お試しください。')
    },
    [clearAvatarBlobUrl, isCurrent, resolveDefaultAvatar]
  )

  const syncAvatar = useCallback(
    async (candidate?: HubSelectionHandoff) => {
      if (isAuthLoading) {
        setIsLoading(true)
        return
      }

      const syncId = activeSyncIdRef.current + 1
      activeSyncIdRef.current = syncId
      setIsLoading(true)
      setErrorMessage(null)
      setHubIssue(null)
      setNoticeMessage(null)

      if (!user?.uid) {
        clearAvatarBlobUrl(syncId)
        setIsLoading(false)
        return
      }

      try {
        const avatarData = await getAvatar()
        if (activeSyncIdRef.current !== syncId) return

        if (!avatarData) {
          await deleteAvatarCache(user.uid)

          await showDefaultAvatar(syncId)
          return
        }

        const { avatarVersion: serverAvatarVersion } = avatarData
        if (avatarData.source === 'vroid_hub') {
          // Hubの本体・署名URLは永続キャッシュへ渡さない。
          try {
            await resolveHubAvatar(syncId, avatarData, candidate)
            return
          } catch (error) {
            if (activeSyncIdRef.current !== syncId) return
            const code = readHubErrorCode(error)
            const { status } = readAvatarFailure(error)
            if (
              isTemporaryFailure(error) &&
              displayedUrlRef.current === blobUrlRef.current &&
              displayedUrlRef.current
            ) {
              setNoticeMessage(
                code === 'HUB_RATE_LIMITED' || status === 429 ? HUB_RATE_LIMIT_MESSAGE : HUB_RETAIN_MESSAGE
              )
              return
            }
            clearAvatarBlobUrl(syncId)
            if (code === 'HUB_AUTH_EXPIRED' || code === 'HUB_TERMS_CHANGED') {
              setHubIssue({
                code,
                modelId: avatarData.vroidHubCharacterModelId ?? '',
                avatarVersion: serverAvatarVersion,
              })
            } else {
              setNoticeMessage(
                code === 'HUB_MODEL_UNAVAILABLE'
                  ? '設定アバターが見つからないか、利用できなくなりました。\n一時的にデフォルトアバターを表示します。'
                  : HUB_FALLBACK_MESSAGE
              )
            }
          }
          // 通知や再認証・同意操作の完了を待たずdefault取得を開始する。
          await showDefaultAvatar(syncId, true)
          return
        }
        try {
          await resolveUploadAvatar(syncId, user.uid, serverAvatarVersion)
          return
        } catch (error) {
          const { status, code } = readAvatarFailure(error)
          reportAvatarFailure('blob_download', status, code)
        }
        if (isCurrent(syncId)) await showDefaultAvatar(syncId)
      } catch (error) {
        if (activeSyncIdRef.current !== syncId) return
        const { status, code } = readAvatarFailure(error)
        reportAvatarFailure('settings', status, code)
        if (isTemporaryFailure(error) && displayedUrlRef.current && displayedUrlRef.current === blobUrlRef.current) {
          setNoticeMessage(status === 429 ? HUB_RATE_LIMIT_MESSAGE : HUB_RETAIN_MESSAGE)
          return
        }
        clearAvatarBlobUrl(syncId)
        setNoticeMessage(HUB_FALLBACK_MESSAGE)
        await showDefaultAvatar(syncId, true)
      } finally {
        if (activeSyncIdRef.current === syncId) {
          setIsLoading(false)
        }
      }
    },
    [clearAvatarBlobUrl, isAuthLoading, isCurrent, resolveHubAvatar, resolveUploadAvatar, showDefaultAvatar, user?.uid]
  )

  useEffect(() => {
    void syncAvatar()

    return () => {
      activeSyncIdRef.current += 1
      clearAvatarBlobUrl()
    }
  }, [clearAvatarBlobUrl, syncAvatar])

  useEffect(() => {
    const handleAvatarUpdated = (event: Event) => {
      const detail: unknown = event instanceof CustomEvent ? event.detail : null
      if (
        typeof detail === 'object' &&
        detail !== null &&
        'hubBlob' in detail &&
        detail.hubBlob instanceof Blob &&
        'modelId' in detail &&
        typeof detail.modelId === 'string' &&
        'avatarVersion' in detail &&
        typeof detail.avatarVersion === 'string'
      ) {
        void syncAvatar({ hubBlob: detail.hubBlob, modelId: detail.modelId, avatarVersion: detail.avatarVersion })
      } else {
        void syncAvatar()
      }
    }

    globalThis.addEventListener(AVATAR_UPDATED_EVENT, handleAvatarUpdated)

    return () => {
      globalThis.removeEventListener(AVATAR_UPDATED_EVENT, handleAvatarUpdated)
    }
  }, [syncAvatar])

  const dismissHubIssue = useCallback(() => setHubIssue(null), [])
  const dismissNotice = useCallback(() => {
    setNoticeMessage(null)
    setErrorMessage(null)
  }, [])

  return {
    isLoading,
    avatarFileUrl,
    errorMessage,
    hubIssue,
    noticeMessage,
    dismissHubIssue,
    dismissNotice,
    onAvatarLoadResult,
  }
}
